import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { faPlus } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import classNames from 'classnames';
import { differenceInCalendarDays, format } from 'date-fns';
import { motion } from 'framer-motion';
import React, {
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useDrop } from 'react-dnd';
import { JobStatus } from '~/generated/graphql';
import { useLocalStorage } from '~/hooks';
import { useBreakpoint } from '~/hooks/useBreakpoint';
import { PlannerViewData } from '~/routes/calendar._index';

// Import RescheduleDetails from types to ensure consistency
import { RescheduleDetails } from './types';
// Import CalendarEvent from hooks to use single source of truth
import type { CalendarEvent } from './hooks';

// Utility functions for consistent cell key generation
const createCellKey = (siteId: string, date: Date | string): string => {
  const dateString = date instanceof Date ? format(date, 'yyyy-MM-dd') : date;
  return `${siteId}:${dateString}`;
};

const createDraftJobKey = (siteId: string, date: Date | string): string => {
  return `draft.job.${createCellKey(siteId, date)}`;
};

// Type definitions for better type safety
type DragItem = {
  siteId: string;
  days?: number;
  jobPreview?: ReactNode;
};

type JobDropZoneProps = {
  children: ReactNode;
  siteId: string;
  siteName: string;
  date: Date;
  /** Optional attribute used for product tours (e.g., Usetiful). Applied to root element. */
  dataUsetiful?: string;
  previewTop?: (days: number) => number;
  onReschedule?: (details: RescheduleDetails) => void;
  onCellClick?: (siteId: string, date: Date, event: React.MouseEvent) => void;
  navigate?: (path: string) => void;
  cellEvents?: CalendarEvent[];
  bookingsHidden?: boolean;
  jobDetail?: boolean;
  isRowExpanded?: boolean;
  onLongPress?: (
    siteId: string,
    date: Date,
    position: { x: number; y: number }
  ) => void;
  setDraftCellId: (cellId: string | null) => void;
  setHighlightedCells: (cells: Set<string>) => void;
  highlightedCells: Set<string>;
  draftCellId: string | null;
  setDesktopDraftJobPill: (
    pill: {
      siteId: string;
      date: Date;
      columnIndex: number;
      siteIndex: number;
    } | null
  ) => void;
  start: Date;
  data: PlannerViewData;
  storage: ReturnType<typeof useLocalStorage>;
  hiddenButtonCell?: { siteId: string; date: string } | null;
  setHiddenButtonCell?: (cell: { siteId: string; date: string } | null) => void;
  setIsInJobCreationFlow?: (value: boolean) => void;
  setJobNameWithEvent: (key: string, value: string) => void;
  location?: { pathname: string };
  onScrollToReveal?: (jobIdOrDate: string | Date, siteId?: string) => void;
  secondCellClickTimeoutRef: React.MutableRefObject<NodeJS.Timeout | null>;
  expandSite?: (siteId: string) => void;
  onDragHover?: (siteId: string | null) => void;
  opacity?: number;
};

export const JobDropZone = React.memo(
  ({
    children,
    siteId,
    siteName,
    date,
    dataUsetiful,
    previewTop,
    onReschedule,
    onCellClick,
    navigate,
    cellEvents = [],
    jobDetail = false,
    isRowExpanded = false,
    onLongPress,
    setDraftCellId,
    setHighlightedCells,
    draftCellId,
    setJobNameWithEvent,
    setDesktopDraftJobPill,
    start,
    data,
    hiddenButtonCell,
    setHiddenButtonCell,
    setIsInJobCreationFlow,
    secondCellClickTimeoutRef,
    expandSite,
    onDragHover,
    opacity = 1,
  }: JobDropZoneProps) => {
    // Touch detection for long press
    const [isLongPressing, setIsLongPressing] = useState(false);
    const [showDraftCard, setShowDraftCard] = useState(false);
    const [draftCardPosition, setDraftCardPosition] = useState<{
      x: number;
      y: number;
    } | null>(null);
    const [isButtonSpinning, setIsButtonSpinning] = useState<boolean>(false);
    // Consolidated timer management
    const timerManager = useRef({
      longPress: null as ReturnType<typeof setTimeout> | null,
      bottomSheet: null as ReturnType<typeof setTimeout> | null,
      cleanup: null as ReturnType<typeof setTimeout> | null,
      loading: null as ReturnType<typeof setTimeout> | null,
      buttonSpinner: null as ReturnType<typeof setTimeout> | null,
    });

    // Single cleanup function for all timers
    const clearAllTimers = useCallback(() => {
      Object.values(timerManager.current).forEach((timer) => {
        if (timer) {
          clearTimeout(timer);
        }
      });
      timerManager.current = {
        longPress: null,
        bottomSheet: null,
        cleanup: null,
        loading: null,
        buttonSpinner: null,
      };
    }, []);
    const touchStartPositionRef = useRef<{ x: number; y: number } | null>(null);
    const dropZoneRef = useRef<HTMLDivElement | null>(null);
    // Track last hovered site to avoid redundant hover calls
    const lastHoveredSiteIdRef = useRef<string | null>(null);

    const { isMobile } = useBreakpoint();

    const [{ days, isOver, canDrop, jobPreview }, drop] = useDrop({
      accept: 'job',
      drop: (item) => {
        const {
          eventId,
          scheduledStart,
          status,
          jobName,
          assigneeName,
          assigneeId,
          siteId: originalSiteId,
        } = item as {
          eventId: string;
          scheduledStart: Date;
          status: string;
          jobName: string;
          assigneeName: string;
          assigneeId?: string;
          siteId: string;
        };
        const dayOffset = differenceInCalendarDays(date, scheduledStart);

        if (status === 'Created') {
          // Created jobs: drag without confirmation
          onReschedule?.({
            jobId: eventId,
            offsetDays: dayOffset,
            originalSiteId,
            targetSiteId: siteId,
          });
        } else if (status === 'Offered') {
          // Offered jobs: trigger dialog to confirm notifying assignee
          const jobStatus = JobStatus[status as keyof typeof JobStatus];
          onReschedule?.({
            jobId: eventId,
            jobStatus,
            jobName,
            assigneeName,
            offsetDays: dayOffset,
            originalDate: scheduledStart,
            targetDate: date,
            originalSiteId,
            targetSiteId: siteId,
          });
        } else if (status === 'Accepted') {
          // Accepted (Confirmed) jobs: check if current user is assignee
          onReschedule?.({
            jobId: eventId,
            jobStatus: JobStatus.Accepted,
            offsetDays: dayOffset,
            assigneeId,
            jobName,
            assigneeName,
            originalDate: scheduledStart,
            targetDate: date,
            originalSiteId,
            targetSiteId: siteId,
          });
        }
      },
      collect: (monitor) => {
        const over = monitor.isOver();
        const can = monitor.canDrop();
        const item = monitor.getItem() as
          | { days?: number; jobPreview?: ReactNode; siteId?: string }
          | undefined;

        return {
          isOver: over,
          canDrop: can,
          days: item?.days,
          jobPreview: item?.jobPreview,
        };
      },
      canDrop: (item: DragItem) => {
        // Only allow dropping on the same site (no cross-site moves)
        // This still allows ghost preview to show in cells with existing jobs on the same site
        return (
          item &&
          typeof item === 'object' &&
          'siteId' in item &&
          item.siteId === siteId
        );
      },
      hover: () => {
        // Track which site is being hovered over for temporary expansion
        if (lastHoveredSiteIdRef.current !== siteId) {
          onDragHover?.(siteId);
          lastHoveredSiteIdRef.current = siteId;
        }
      },
    });

    // Touch event handlers for long press
    const handleTouchStart = useCallback(
      (e: React.TouchEvent) => {
        const touch = e.touches[0];
        touchStartPositionRef.current = { x: touch.clientX, y: touch.clientY };

        // Calculate position within the cell
        if (dropZoneRef.current) {
          const rect = dropZoneRef.current.getBoundingClientRect();
          const relativeX = touch.clientX - rect.left;
          const relativeY = touch.clientY - rect.top;
          setDraftCardPosition({ x: relativeX, y: relativeY });
        }

        // Start long press timer
        timerManager.current.longPress = setTimeout(() => {
          setIsLongPressing(true);
          setShowDraftCard(true);

          // Haptic feedback when draft card appears
          if ('vibrate' in navigator) {
            navigator.vibrate(50); // Short vibration for draft card
          }

          // Trigger bottom sheet after delay
          if (onLongPress && touchStartPositionRef.current) {
            timerManager.current.bottomSheet = setTimeout(() => {
              // Haptic feedback when bottom sheet opens
              if ('vibrate' in navigator) {
                navigator.vibrate(100); // Longer vibration for bottom sheet
              }
              onLongPress(siteId, date, touchStartPositionRef.current!);

              // Clean up draft card after 400ms
              timerManager.current.cleanup = setTimeout(() => {
                setShowDraftCard(false);
                setDraftCardPosition(null);
                setIsLongPressing(false);
              }, 1000);
            }, 500);
          }
        }, 200); // 200ms for draft card to appear
      },
      [onLongPress, siteId, date]
    );

    const handleTouchEnd = useCallback(() => {
      // Only clear the initial long press timer if draft card hasn't appeared yet
      if (timerManager.current.longPress && !showDraftCard) {
        clearTimeout(timerManager.current.longPress);
        timerManager.current.longPress = null;
      }

      // Don't clear bottom sheet timer or cleanup timer - let them complete
      // This allows the bottom sheet to open and draft card to persist

      // Only reset states if we didn't complete the long press
      if (!showDraftCard) {
        setIsLongPressing(false);
        setDraftCardPosition(null);
        touchStartPositionRef.current = null;
      }
    }, [showDraftCard]);

    const handleTouchMove = useCallback((e: React.TouchEvent) => {
      if (!touchStartPositionRef.current) return;

      const touch = e.touches[0];
      const deltaX = Math.abs(touch.clientX - touchStartPositionRef.current.x);
      const deltaY = Math.abs(touch.clientY - touchStartPositionRef.current.y);
      const threshold = 10; // pixels

      // If user has moved significantly, cancel long press
      if (deltaX > threshold || deltaY > threshold) {
        // Clear all timers
        clearAllTimers();

        // Reset states
        setIsLongPressing(false);
        setShowDraftCard(false);
        setDraftCardPosition(null);
        touchStartPositionRef.current = null;
      }
    }, []);

    const handleTouchCancel = useCallback(() => {
      // Clear all timers on cancel (this is a cancellation, not completion)
      clearAllTimers();

      // Reset states
      setIsLongPressing(false);
      setShowDraftCard(false);
      setDraftCardPosition(null);
      touchStartPositionRef.current = null;
    }, []);

    // Cleanup timer on unmount only
    useEffect(() => {
      return () => {
        clearAllTimers();
      };
    }, []); // Empty deps - only run on unmount

    const handleCreateJobClick = (event: React.MouseEvent) => {
      event.stopPropagation(); // Prevent triggering cell click

      const cellId = createCellKey(siteId, date);

      // Show spinner immediately
      setIsButtonSpinning(true);

      // Check if we're already creating a job in this exact cell
      const isSameCell = draftCellId === cellId;

      // Check if we already have a draft in a different cell
      const hasExistingDraft = draftCellId && draftCellId !== cellId;

      if (hasExistingDraft) {
        // Second cell clicked - add to highlighted cells, keep pill in original cell

        setHighlightedCells(new Set([draftCellId, cellId]));

        // Set timeout to clear second highlight if user doesn't navigate (chooses "Keep Changes")
        if (secondCellClickTimeoutRef.current) {
          clearTimeout(secondCellClickTimeoutRef.current);
        }
        secondCellClickTimeoutRef.current = setTimeout(() => {
          setHighlightedCells(new Set([draftCellId]));
          setJobNameWithEvent(`draft.job.${cellId}`, '');
        }, 2000); // 2 second timeout
      } else if (!isSameCell) {
        // First cell clicked or switching to new cell

        setDraftCellId(cellId);
        setHighlightedCells(new Set([cellId]));

        // Show desktop draft job pill immediately (state-based mode)
        const columnIndex = differenceInCalendarDays(date, start);
        const siteIndex = data.findIndex(
          (site: PlannerViewData[number]) => site.id === siteId
        );
        setDesktopDraftJobPill({ siteId, date, columnIndex, siteIndex });
      }

      // Don't clear hiddenButtonCell here - let the timeout handle it

      // Set job creation flow state
      if (setIsInJobCreationFlow) {
        setIsInJobCreationFlow(true);
      }

      // Expand the row if it's collapsed to make space for the job pill
      // This ensures the extra space is visible when creating a job
      if (!isRowExpanded && expandSite) {
        expandSite(siteId);
      }

      // Set initial job name for the pill - always start fresh for new cell clicks
      setJobNameWithEvent(createDraftJobKey(siteId, date), '');

      // Hide button for this cell after 500ms
      if (setHiddenButtonCell) {
        timerManager.current.buttonSpinner = setTimeout(() => {
          setHiddenButtonCell({ siteId, date: format(date, 'yyyy-MM-dd') });
          setIsButtonSpinning(false);
        }, 500);
      }

      // Navigate immediately - Prompt will block if needed
      if (navigate) {
        navigate(
          `/jobs/planner/new?siteId=${siteId}&date=${format(
            date,
            'yyyy-MM-dd'
          )}`
        );
      } else {
        // Fallback to window.location if navigate is not provided
        window.location.href = `/jobs/planner/new?siteId=${siteId}&date=${format(
          date,
          'yyyy-MM-dd'
        )}`;
      }
    };

    // Memoize button position classes to avoid recalculation on every render
    const buttonPositionClasses = useMemo(() => {
      return classNames(
        jobDetail || isRowExpanded ? 'bottom-2' : 'bottom-0.5',
        'absolute left-1/2 transform -translate-x-1/2 flex items-center justify-center'
      );
    }, [jobDetail, isRowExpanded]); // Recalculate when jobDetail or isRowExpanded changes

    // Memoize button visibility to avoid recalculation on every render
    const shouldShowButton = useMemo(() => {
      const visibleJobs = cellEvents.filter(
        ({ event }) => event.status !== 'Booking'
      );

      // Hide button if this cell is currently hidden
      if (
        hiddenButtonCell &&
        hiddenButtonCell.siteId === siteId &&
        hiddenButtonCell.date === format(date, 'yyyy-MM-dd')
      ) {
        return false;
      }

      // If no jobs, always show button
      if (visibleJobs.length === 0) {
        return true;
      }

      // If there are jobs but row is not expanded, don't show button (no whitespace)
      if (visibleJobs.length > 0 && !isRowExpanded) {
        return false;
      }

      // If there are jobs and row is expanded, show button (there's whitespace)
      return true;
    }, [cellEvents, hiddenButtonCell, siteId, date, isRowExpanded]);

    const buttonConfig = buttonPositionClasses;

    // Button visibility is now handled by shouldShowButton() function

    // Guard against state updates after unmount
    const isMounted = useRef(true);
    useEffect(() => {
      isMounted.current = true;
      return () => {
        isMounted.current = false;
        clearAllTimers();
      };
    }, []); // Empty deps - only run on unmount

    const handleKeyDown = useCallback(
      (e: React.KeyboardEvent<HTMLDivElement>) => {
        if (!onCellClick) return;
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onCellClick(siteId, date, e as unknown as React.MouseEvent);
        }
      },
      [onCellClick, siteId, date]
    );

    return (
      <div
        className={classNames(
          'group relative touch-manipulation select-none',
          'cursor-pointer',
          'h-full w-full',
          opacity === 0.4 && 'bg-gray-300'
        )}
        ref={(node) => {
          drop(node);
          dropZoneRef.current = node;
        }}
        {...(dataUsetiful ? { ['data-usetiful']: dataUsetiful } : {})}
        {...(dataUsetiful
          ? {
              role: 'button',
              tabIndex: 0,
              ['aria-label']: `Open day view for ${siteName} on ${format(
                date,
                'yyyy-MM-dd'
              )}`,
            }
          : {})}
        onDragOver={(e: React.DragEvent<HTMLDivElement>) => {
          // Firefox requires preventing default on dragover to allow dropping
          e.preventDefault();
          try {
            if (e.dataTransfer) {
              e.dataTransfer.dropEffect = 'move';
            }
          } catch {
            /* empty */
          }
        }}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        onTouchCancel={handleTouchCancel}
        onKeyDown={handleKeyDown}
        onContextMenu={(e) => e.preventDefault()} // Prevent right-click menu
        onClick={(e) => {
          // Trigger cell click on both desktop and mobile if not long-pressing
          if (!isLongPressing && onCellClick) {
            onCellClick(siteId, date, e);
          }
        }}
        style={{
          userSelect: 'none',
          WebkitUserSelect: 'none',
          MozUserSelect: 'none',
          msUserSelect: 'none',
          touchAction: 'manipulation',
          opacity: opacity,
        }}
      >
        {children}

        {/* Draft Job Card - appears on long press */}
        {showDraftCard && draftCardPosition && (
          <motion.div
            initial={{ opacity: 0, scaleX: 0.5 }}
            animate={{ opacity: 1, scaleX: 1 }}
            exit={{ opacity: 0, scale: 0.8 }}
            transition={{ duration: 0.2, ease: 'easeOut' }}
            className={classNames(
              'absolute z-10 rounded-sm bg-gray-100',
              'border border-gray-200'
            )}
            style={{
              left: 0, // Full width
              top: draftCardPosition.y - 15, // Use touch Y position
              width: '100%', // Take full cell width
              height: 30,
            }}
          >
            <div className='mx-2 rounded-lg border border-gray-200 bg-white px-3 py-2'>
              <div className='flex items-center justify-center'>
                <span className='select-none text-xs font-medium text-gray-700'>
                  New Job
                </span>
              </div>
            </div>
          </motion.div>
        )}

        {/* Hover + Button */}
        {shouldShowButton && !isMobile && (
          <div
            className={classNames(
              'add-job-button-container pointer-events-none z-20 opacity-0 transition-opacity duration-200 group-hover:pointer-events-auto group-hover:opacity-100',
              buttonConfig
            )}
          >
            <button
              className={classNames(
                jobDetail || isRowExpanded ? 'h-8 w-8' : 'h-7 w-7',
                'flex items-center justify-center rounded-full transition-all duration-150',
                'text-gray-600 hover:scale-105 active:scale-95 active:bg-gray-300',
                {
                  'bg-gray-400 hover:bg-gray-400': isButtonSpinning,
                  'bg-gray-100 hover:bg-gray-200': !isButtonSpinning,
                }
              )}
              type='button'
              aria-label='Add job'
              title='Add job'
              onClick={handleCreateJobClick}
            >
              {isButtonSpinning ? (
                <svg
                  className='h-5 w-5 animate-spin'
                  xmlns='http://www.w3.org/2000/svg'
                  fill='none'
                  viewBox='0 0 24 24'
                >
                  <circle
                    className='opacity-25'
                    cx='12'
                    cy='12'
                    r='10'
                    stroke='currentColor'
                    strokeWidth='3'
                  ></circle>
                  <path
                    className='opacity-75'
                    fill='currentColor'
                    d='M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z'
                  ></path>
                </svg>
              ) : (
                <FontAwesomeIcon
                  icon={faPlus as IconProp}
                  className='h-5 w-5'
                />
              )}
            </button>
          </div>
        )}

        {canDrop && previewTop && days !== undefined && (
          <div
            className={classNames(
              'pointer-events-none absolute transition-opacity',
              {
                'opacity-0': !isOver,
              }
            )}
            style={{
              top: previewTop(days),
              zIndex: 20, // Above all job pills, expand buttons, and draft pills
            }}
          >
            {jobPreview}
          </div>
        )}
      </div>
    );
  },
  (prevProps, nextProps) => {
    // Custom comparison function for React.memo
    // Only re-render if these critical props change

    // Check Set equality by comparing size and contents
    const highlightedCellsEqual = (() => {
      const prev = prevProps.highlightedCells;
      const next = nextProps.highlightedCells;
      if (!prev && !next) return true;
      if (!prev || !next) return false;
      if (prev.size !== next.size) return false;
      // Check every entry in prev exists in next
      for (const item of prev) {
        if (!next.has(item)) return false;
      }
      return true;
    })();

    return (
      prevProps.siteId === nextProps.siteId &&
      prevProps.date.getTime() === nextProps.date.getTime() &&
      prevProps.bookingsHidden === nextProps.bookingsHidden &&
      prevProps.jobDetail === nextProps.jobDetail &&
      prevProps.isRowExpanded === nextProps.isRowExpanded &&
      highlightedCellsEqual &&
      prevProps.draftCellId === nextProps.draftCellId &&
      prevProps.cellEvents === nextProps.cellEvents &&
      prevProps.hiddenButtonCell?.siteId ===
        nextProps.hiddenButtonCell?.siteId &&
      prevProps.hiddenButtonCell?.date === nextProps.hiddenButtonCell?.date
    );
  }
);

JobDropZone.displayName = 'JobDropZone';
